/**
 * Five taps a person in a seat can send to the wall at the front of the room (GH-781).
 *
 * ## What it is, and what it deliberately is not
 *
 * One tap, one pulse on the hall's projection. It is fire-and-forget *in feel* — the confirmation
 * a donor is looking for is on the wall, not on their phone — but a refused send (a rate limit, a
 * dead connection) says so rather than swallowing the tap, because a control that silently does
 * nothing is one a person presses harder and then distrusts.
 *
 * Nothing about the sender survives the request. The public read the display performs carries a
 * kind and a sequence number and no identifier of any sort, which is why this is the one donor
 * action whose response has no state worth parsing. The rate limit lives in the repository, so a
 * fast thumb is *answered*, never counted twice.
 *
 * ## Why it lives in its own file
 *
 * Two hosts: the directory's donor sheet, where it sits under the event menu, and the event page,
 * where it is pinned above Give. Both render it only for a donor who is checked in *here* — the
 * server enforces that with `requireCheckedIn` and would refuse anyway, but offering a control
 * that is going to be refused is the thing this product does not do.
 */
import { useState } from "react";
import type { ReactionKindWire } from "@givehere/contracts";
import { DonorApiError, sendReaction } from "./donor-session.js";

/**
 * The five, and the labels a screen reader hears.
 *
 * A deliberately mixed set: applause and celebration belong to any gathering, and the raised
 * hands are one gesture of prayer among the traditions this platform serves rather than a
 * statement that it serves one. `CLAUDE.md` forbids hard-coding a single faith into donor-facing
 * copy; what it asks for instead is a set broad enough that no organization has to explain the
 * absence of its own.
 */
const REACTIONS: readonly { kind: ReactionKindWire; emoji: string; label: string }[] = [
  { kind: "heart", emoji: "❤️", label: "Send a heart" },
  { kind: "clap", emoji: "👏", label: "Applaud" },
  { kind: "tada", emoji: "🎉", label: "Celebrate" },
  { kind: "dua", emoji: "🤲", label: "Make dua" },
  { kind: "laugh", emoji: "😂", label: "Laugh" },
];

export function ReactionBar({ token, eventId }: { token: string; eventId: string }) {
  const [note, setNote] = useState<string | null>(null);

  const tap = (kind: ReactionKindWire) => {
    setNote(null);
    sendReaction(token, eventId, kind).catch((caught: unknown) =>
      setNote(caught instanceof DonorApiError ? caught.message : "That didn't send — try again."),
    );
  };

  return (
    <div className="reactbar">
      <span className="reactbar__hint">React on the big screen</span>
      <span className="reactbar__row">
        {REACTIONS.map((reaction) => (
          <button
            key={reaction.kind}
            type="button"
            className="reactbar__button"
            aria-label={reaction.label}
            onClick={() => tap(reaction.kind)}
          >
            {reaction.emoji}
          </button>
        ))}
      </span>
      {note && (
        <p className="notice notice--error" role="alert">
          {note}
        </p>
      )}
    </div>
  );
}
